// Configuration for the storefront Search page presentation. Persisted as a
import { IAutocompleteConfig } from './autocompleteConfig';
// single JSON string on IStorefrontSettings.searchPageConfig so the schema-bound
// backend only needs one new field (rather than ~20 scalar style fields), and so
// it survives buildStorefrontPatch (which only diffs primitives and arrays).

export type SearchResultViewMode = 'rows' | 'cards';

// Where the facet-filter controls render on the storefront search page.
// 'panel' = a side panel beside the results; 'inline' = a row of menu controls
// in the breadcrumb/chip area above the results.
export type FacetSidebarLayout = 'panel' | 'inline';

// Shopper-facing facet filtering. The storefront only surfaces the filters once a
// segment AND category are selected, then shows the storefront's *searchable*
// attributes (aggregated over that segment/category subset) as filter controls.
export interface IFacetSidebarConfig {
    enabled?: boolean;
    layout?: FacetSidebarLayout;
    // Include a price-range filter alongside the attribute filters.
    priceFilter?: boolean;
}

// The named fields below get first-class controls in the editor and a bit of smart
// defaulting in the builders. The index signature lets "Raw JSON" mode add any
// additional CSS property (camelCase, e.g. boxShadow / filter / transition) — those
// pass straight through to the element on the storefront via importantStyleRef.
export interface ISearchCardStyle {
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
    borderStyle?: string;
    borderRadius?: number;
    boxShadow?: string;
    [prop: string]: string | number | undefined;
}

export interface ISearchTextStyle {
    font?: string;
    fontSize?: number; // percent, matching the storefront's % font sizing
    fontWeight?: string;
    color?: string;
    // Unitless multiple of the font size, held as a STRING ('1.5') — a number would be emitted as a
    // px length by cssText / the storefront's importantStyleRef. See parseLineHeight.
    lineHeight?: string;
    [prop: string]: string | number | undefined;
}

export interface ISearchPriceStyle extends ISearchTextStyle {
    // The price can optionally be wrapped in its own styled container ("div").
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
    borderRadius?: number;
    padding?: string;
}

// The box that wraps the whole results grid (the `.card-container`). Background,
// border and inner padding styling; card spacing lives in cardGap.
export interface ISearchContainerStyle {
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
    borderStyle?: string;
    borderRadius?: number;
    padding?: number;
    [prop: string]: string | number | undefined;
}

// Generic text + box style used for the paginator and breadcrumb elements (each of
// which has a container plus repeated cells/items).
export interface ISearchElementStyle {
    font?: string;
    fontSize?: number; // percent
    fontWeight?: string;
    color?: string;
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
    borderStyle?: string;
    borderRadius?: number;
    // A bare number is px, but merchants routinely author the CSS shorthand instead
    // ("0.5em 1.5em" on a banner CTA, so the padding tracks the button's font size).
    padding?: string | number;
    // First-class controls for a few common camelCase CSS props (also valid via the
    // passthrough index signature below); named here so the editor controls can read them.
    textTransform?: string;
    textAlign?: string;
    letterSpacing?: string;
    // Unitless multiple of the font size, held as a STRING ('1.5') — see ISearchTextStyle.lineHeight.
    lineHeight?: string;
    boxShadow?: string;
    [prop: string]: string | number | undefined;
}

export interface ISearchPageConfig {
    // The search page's own search bar's suggestion popout — enabled, columns and styling entirely
    // independent of the header bar's.
    searchAutocomplete?: IAutocompleteConfig;
    viewMode?: SearchResultViewMode;
    // Gap between cards in the results grid, in px (falls back to the CSS default, 16).
    cardGap?: number;
    card?: ISearchCardStyle;
    title?: ISearchTextStyle;
    price?: ISearchPriceStyle;
    // The product image area within each card/row (background tint, border, radius, padding).
    image?: ISearchElementStyle;
    container?: ISearchContainerStyle;
    // Paginator: the menu box (container) and each page button (cell).
    paginatorContainer?: ISearchElementStyle;
    paginatorCell?: ISearchElementStyle;
    // Filter breadcrumbs: the row (container) and each chip (item).
    breadcrumbContainer?: ISearchElementStyle;
    breadcrumbItem?: ISearchElementStyle;
    // Shopper-facing facet filtering (attribute + optional price filters).
    facetSidebar?: IFacetSidebarConfig;
    // When true, the search chrome (filter panel + search bar + paginator) stays pinned
    // while scrolling so only the results scroll underneath.
    stickyControls?: boolean;
    // The search keyword input box (Fluent Input root).
    searchBar?: ISearchElementStyle;
    // The facet-filter side panel container (the 'panel' layout aside).
    facetPanel?: ISearchElementStyle;
    // The facet section headings inside the panel (each attribute group's title, e.g. "RAM").
    facetHeading?: ISearchTextStyle;
    // The circular "add to cart" button shown on each result (rows + cards views).
    addToCartButton?: ISearchElementStyle;
    // The "Add to Cart" text label beside that button (rows view). Defaults to a white pill with
    // green text; override to fit non-green themes.
    addToCartLabel?: ISearchElementStyle;
    // Hover styling for the product tile (cards + rows), applied as a scoped `:hover` rule
    // with a short auto-transition. Any CSS passes through (boxShadow, transform, etc.).
    cardHover?: ISearchElementStyle;
    // Per-result-row meta chips (rows view). The whole "Category: A > B > C" breadcrumb
    // and the whole "Manufacturer: X" label each render as a single styled chip (bg,
    // border, radius, padding, font). The clickable links inside keep their own behavior.
    categoryChip?: ISearchElementStyle;
    manufacturerChip?: ISearchElementStyle;
}

export const DEFAULT_SEARCH_PAGE_CONFIG: ISearchPageConfig = {
    viewMode: 'rows',
};

export function parseSearchPageConfig(raw?: string): ISearchPageConfig {
    if (!raw) return { ...DEFAULT_SEARCH_PAGE_CONFIG };
    try {
        const parsed = JSON.parse(raw) as ISearchPageConfig;
        return { ...DEFAULT_SEARCH_PAGE_CONFIG, ...parsed };
    } catch {
        return { ...DEFAULT_SEARCH_PAGE_CONFIG };
    }
}

export function serializeSearchPageConfig(config: ISearchPageConfig): string {
    return JSON.stringify(config);
}
